/**
 * How a thread's status is spoken to the user.
 *
 * The domain says `open` / `accepted` / `rejected` and will keep saying it —
 * those values are the record, and the backend, the git history and the
 * decision log all use them. But the buttons say Apply and Discard, so
 * showing "rejected" next to a draft the user discarded reads like something
 * else happened. This maps the stored value onto the words the UI already
 * uses, at the point of display only.
 */
const LABELS: Record<string, string> = {
    open: "open",
    accepted: "applied",
    rejected: "discarded",
};

export const threadStatusLabel = (status: string): string =>
    LABELS[status] ?? status;

export const threadStatusSeverity = (status: string): string => {
    switch (status) {
        case "accepted":
            return "success";
        case "rejected":
            return "danger";
        default:
            return "info";
    }
};

/**
 * Whether `thread` is an open draft this user authored — the one rule shared by
 * every author-only action on a draft (rename, the delete-attempt on Leave,
 * Discard).
 */
export const isOwnOpenDraft = (
    thread: {
        status: string;
        created_by?: { type: string; id: string } | null;
    },
    userId?: string
): boolean =>
    thread.status === "open" &&
    thread.created_by?.type === "user" &&
    thread.created_by.id === userId;

export const renameBlockedReason = (
    thread: Parameters<typeof isOwnOpenDraft>[0],
    userId?: string
): string | undefined => {
    if (isOwnOpenDraft(thread, userId)) return undefined;
    if (thread.status !== "open") return "Only open drafts can be renamed";
    return "Only the author can rename this draft";
};

/**
 * The resolution entry's text comes from the backend as
 * "Thread accepted: …" / "Thread rejected: …" (resolve_thread.py). Re-say it
 * in the same vocabulary rather than leaking the domain word into the
 * conversation.
 */
export const humanizeResolutionContent = (content: string): string =>
    content
        .replace(/^Thread accepted\b/, "Draft applied")
        .replace(/^Thread rejected\b/, "Draft discarded");
